import { SupportedCountryCurrency } from '@/types/supported-country-currency';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { formatPercentage } from '@/utils/formatting/format-percentage';
import { ChevronRight, Info } from 'lucide-react';
import { ComponentProps } from 'react';
import { Card } from '../../../design-system/card/card';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { Tooltip } from '../../../design-system/tooltip/tooltip';
import { cn } from '../../../utils/cn';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { getReturnColorClass } from '../utils/get-return-color-class';
import { ReturnData } from '../utils/get-return-data';

type YieldCardConfig = {
  variant: 'YIELD';
  yield: number;
  annualFees: number;
  netRate: number;
};

type ReturnCardConfig = {
  variant: 'RETURN';
  returnData: ReturnData;
  currency: SupportedCountryCurrency;
};

type MetricCardProps = {
  cardConfig: YieldCardConfig | ReturnCardConfig;
  className?: string;
};

export const MetricCard = ({ cardConfig, className }: MetricCardProps) => {
  const { label, value } = getContent(cardConfig);

  const returnColorClass =
    cardConfig.variant === 'RETURN'
      ? getReturnColorClass(cardConfig.returnData.monthlyReturns[0].totalReturn)
      : undefined;

  return (
    <Card className={cn('min-w-[180px] shrink-0 grow basis-0 self-stretch', className)}>
      <Stack space={3} className="p-6">
        <Inline space={1} wrap="nowrap">
          <p className="text-sm font-normal text-muted-foreground">{label}</p>

          {cardConfig.variant === 'YIELD' && (
            <Tooltip
              trigger={
                <button type="button" className="inline-flex items-center">
                  <Info className="size-3 text-muted-foreground" />
                </button>
              }
              content={
                <Stack space={1}>
                  <p className="text-sm">Gross rate: {formatPercentage(cardConfig.yield)}</p>
                  <p className="text-sm">Annual fees: {formatPercentage(cardConfig.annualFees)}</p>
                  <p className="text-sm">Net rate: {formatPercentage(cardConfig.netRate)}</p>
                </Stack>
              }
            />
          )}
        </Inline>

        <Inline wrap="nowrap" space={1}>
          <p className={cn('text-2xl font-medium text-foreground', returnColorClass)}>{value}</p>

          {cardConfig.variant === 'RETURN' && <ChevronRight className={cn('h-6 w-6', returnColorClass)} />}
        </Inline>
      </Stack>
    </Card>
  );
};

const getContent = (cardConfig: ComponentProps<typeof MetricCard>['cardConfig']) => {
  const { variant } = cardConfig;

  switch (variant) {
    case 'YIELD': {
      const value = formatPercentage(cardConfig.netRate);

      return {
        label: 'Current yield',
        value,
        ariaLabel: `Current yield is ${value}`,
      };
    }
    case 'RETURN': {
      const value = getAmountWithCurrency({
        amount: cardConfig.returnData.monthlyReturns[0].totalReturn,
        currency: cardConfig.currency,
        isAccountBalance: true,
      });

      return {
        label: `Returns in ${cardConfig.returnData.monthlyReturns[0].label}`,
        value,
        ariaLabel: `Return is ${value}`,
      };
    }
    default:
      return exhaustiveCheck(variant);
  }
};
